import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import MiniGraph from '../../components/lineGraph/mini';
import { allWeaponsStats, PERIODS } from '../../lib/partUsageStats';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Fresh vnodes per render (never reuse element instances across renders).
function periodLabel(key) {
    switch (key) {
        case 'month': return <Text id='part.stats-period-month'>This month</Text>;
        case '3m': return <Text id='part.stats-period-3m'>Last 3 months</Text>;
        case '12m': return <Text id='part.stats-period-12m'>Last 12 months</Text>;
        case 'year': return <Text id='part.stats-period-year'>This year</Text>;
        default: return <Text id='part.stats-period-all'>All time</Text>;
    }
}
const DEFAULT_CHART_WIDTH = 960;
const MIN_CHART_WIDTH = 320;
// Each chart sits inside a `p-3` box (1rem padding per side); the SVG is
// border-box sized, so subtract the box padding to avoid a horizontal scroll.
const BOX_PADDING_X = 32;
// Touch devices fire an emulated mouseenter right before the tap's click;
// ignore hover for a moment after a touch so a tap toggles cleanly.
const TOUCH_HOVER_GRACE_MS = 700;

function monthYearTick(val) {
    const d = new Date(val);
    return `${MONTHS[d.getMonth()]} '${d.getFullYear().toString().slice(2)}`;
}

function dayMonthTick(val) {
    const d = new Date(val);
    return `${d.getDate()}. ${MONTHS[d.getMonth()]}`;
}

function monthTick(val) {
    return MONTHS[new Date(val).getMonth()];
}

function formatNumber(num) {
    return (num || 0).toLocaleString('nb-NO');
}

function seriesStyle(weapon, activeId) {
    const isActive = activeId === weapon.id;
    const dimmed = activeId && !isActive;
    let style = `stroke: ${weapon.color} !important; stroke-width: ${isActive ? 3.5 : 2}px !important;`;
    if (weapon.dash.svg) {
        style += ` stroke-dasharray: ${weapon.dash.svg};`;
    }
    if (dimmed) {
        style += ' stroke-opacity: 0.15;';
    }
    return style;
}

/**
 * Full-height drawer (drawer name: 'partStats') with the shot-count graphs
 * for every weapon in a parts list at once. Opened from the graph button in
 * the parts list header. The parts API embeds each part's usage entries, so
 * nothing is fetched here. Only parts tagged pistol/rifle are included (see
 * lib/partUsageStats.js).
 *
 * Props: parts (the part objects currently listed; falls back to
 * partStore.parts when omitted).
 */
@observer
class PartStatsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chartWidth: DEFAULT_CHART_WIDTH,
            mode: 'cumulative', // 'cumulative' (running total) | 'weekly' (shots per week)
            period: 'all',      // one of PERIODS
            hoverId: null,   // weapon under the mouse (transient)
            pinnedId: null,  // weapon tapped/clicked (sticky)
        };
        this.containerRef = null;
        this.lastTouchAt = 0;
    }

    componentDidMount() {
        this.measure();
        // The drawer slides in; measure again once its layout has settled.
        this.measureTimer = setTimeout(this.measure, 400);
        window.addEventListener('resize', this.measure);
    }

    componentWillUnmount() {
        clearTimeout(this.measureTimer);
        window.removeEventListener('resize', this.measure);
    }

    measure = () => {
        const width = this.containerRef?.clientWidth;
        if (width > 0) {
            const chartWidth = Math.max(MIN_CHART_WIDTH, Math.floor(width) - BOX_PADDING_X);
            if (chartWidth !== this.state.chartWidth) {
                this.setState({ chartWidth });
            }
        }
    }

    setMode = e => {
        const { mode } = e.currentTarget.dataset;
        if (mode && mode !== this.state.mode) {
            this.setState({ mode });
        }
    }

    setPeriod = e => {
        const { period } = e.currentTarget.dataset;
        if (PERIODS.includes(period) && period !== this.state.period) {
            this.setState({ period });
        }
    }

    noteTouch = () => {
        this.lastTouchAt = Date.now();
    }

    hoverWeapon = e => {
        if (Date.now() - this.lastTouchAt < TOUCH_HOVER_GRACE_MS) {
            return;
        }
        this.setState({ hoverId: parseInt(e.currentTarget.dataset.id, 10) });
    }

    unhoverWeapon = () => {
        if (this.state.hoverId !== null) {
            this.setState({ hoverId: null });
        }
    }

    toggleWeapon = e => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        this.setState(prev => ({ pinnedId: prev.pinnedId === id ? null : id }));
    }

    viewPart = e => {
        e.preventDefault();
        e.stopPropagation();
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('viewPart', {
            id,
            height: drawerHeightMediumLarge,
            skipUpdate: true,
        }, drawerLevel + 1);
    }

    getParts() {
        const { parts } = this.props;
        if (Array.isArray(parts)) {
            return parts;
        }
        const { partStore } = this.props.stores;
        return partStore.parts || [];
    }

    getActiveId() {
        const { hoverId, pinnedId } = this.state;
        return hoverId || pinnedId || null;
    }

    renderLegend(weapons) {
        const activeId = this.getActiveId();
        return (
            <div class='row g-1 mt-2' onTouchStart={this.noteTouch}>
                {weapons.map(weapon => {
                    const isActive = activeId === weapon.id;
                    const dimmed = activeId && !isActive;
                    return (
                        <div class='col-12 col-md-6' key={weapon.id}>
                            <div
                                class={`d-flex align-items-center rounded-lg px-2 py-1 ${isActive ? 'bg-body-secondary fw-bold' : ''}`}
                                style={`cursor: pointer;${dimmed ? ' opacity: 0.45;' : ''}`}
                                role='button'
                                data-id={weapon.id}
                                onMouseEnter={this.hoverWeapon}
                                onMouseLeave={this.unhoverWeapon}
                                onClick={this.toggleWeapon}
                            >
                                <span
                                    class='flex-shrink-0 me-2'
                                    style={`width: 24px; height: 0; border-top: 3px ${weapon.dash.css} ${weapon.color};`}
                                />
                                <span class='flex-fill'>{weapon.name}</span>
                                <span class='ms-2 text-nowrap'>
                                    <i class='fa-duotone fa-gun me-1 text-body-secondary' />
                                    {formatNumber(weapon.total)}
                                </span>
                                {weapon.lastUsed && (
                                    <small class='ms-2 text-body-secondary text-nowrap d-none d-lg-inline'>
                                        <Text id='part.stats-last-used'>Last used</Text> {util.isoDate(weapon.lastUsed, false, false, true)}
                                    </small>
                                )}
                                <Localizer>
                                    <button
                                        type='button'
                                        class='btn btn-link btn-sm p-0 ms-2 text-body-secondary'
                                        data-id={weapon.id}
                                        onClick={this.viewPart}
                                        title={<Text id='part.stats-open'>Open part</Text>}
                                    >
                                        <i class='fa-regular fa-arrow-up-right-from-square' />
                                    </button>
                                </Localizer>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderPeriodSelector() {
        const { period } = this.state;
        return (
            <div class='d-flex flex-wrap align-items-center gap-1 mt-2'>
                <span class='text-body-secondary small me-1'>
                    <i class='fa-regular fa-calendar-range me-1' />
                    <Text id='part.stats-period'>Period</Text>:
                </span>
                {PERIODS.map(key => (
                    <button
                        key={key}
                        type='button'
                        class={`btn btn-sm rounded-pill ${period === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        data-period={key}
                        onClick={this.setPeriod}
                    >
                        {periodLabel(key)}
                    </button>
                ))}
            </div>
        );
    }

    renderModeToggle() {
        const { mode } = this.state;
        const modes = [
            { key: 'cumulative', label: <Text id='part.stats-mode-cumulative'>Cumulative</Text> },
            { key: 'weekly', label: <Text id='part.stats-mode-weekly'>Weekly</Text> },
        ];
        return (
            <div class='btn-group btn-group-sm' role='group'>
                {modes.map(item => (
                    <button
                        key={item.key}
                        type='button'
                        class={`btn rounded-pill ${mode === item.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        data-mode={item.key}
                        onClick={this.setMode}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
        );
    }

    renderUsageChart(stats, chartWidth, darkmode) {
        const { mode } = this.state;
        const isWeekly = mode === 'weekly';
        // A single point draws nothing, so only weapons with two or more points get a line.
        const series = stats.weapons
            .map(weapon => ({ weapon, data: isWeekly ? weapon.weekly : weapon.cumulative }))
            .filter(item => item.data.length > 1);
        if (series.length === 0) {
            return null;
        }
        const activeId = this.getActiveId();
        const chartHeight = chartWidth < 576 ? 240 : 340;
        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-wrap justify-content-between align-items-center mb-1'>
                    <div class='font-weight-lighter text-body-secondary'>
                        {isWeekly
                            ? <Text id='part.shotCountWeekly'>Shots per week</Text>
                            : <Text id='part.shotCountGraph'>Shot count over time</Text>}
                    </div>
                    {this.renderModeToggle()}
                </div>
                <MiniGraph
                    stores={this.props.stores}
                    dataLeft={series.map(item => item.data)}
                    legendLeft={[]}
                    width={chartWidth}
                    height={chartHeight}
                    xTicks={stats.ticks}
                    xTicksVal={stats.tickUnit === 'week' ? dayMonthTick : monthYearTick}
                    yTicksLeftVal={val => Math.round(val)}
                    showYTicksRight={0}
                    paddingLeft={50}
                    paddingRight={20}
                    paddingBottom={15}
                    yTicksLeftWidth={'45px'}
                    xTicksStyle={'font-size: 11px; font-weight: 400; color: #5f6368 !important;'}
                    yTicksLeftStyle={'color: #5f6368 !important;'}
                    inputStyleLeft={series.map(item => seriesStyle(item.weapon, activeId))}
                    // Smoothing overshoots on spiky weekly data; keep weekly lines straight.
                    smoothLeft={series.map(() => !isWeekly)}
                    gradientFillLeft={[]}
                />
                {this.renderLegend(stats.weapons)}
                <div class='text-body-secondary small mt-2'>
                    {isWeekly
                        ? <Text id='part.stats-weekly-help'>Each line is the number of shots per week per weapon. Weeks without shots show as 0.</Text>
                        : <Text id='part.stats-cumulative-help'>Each line is the running total of shots per weapon over time.</Text>}
                    {' '}
                    <Text id='part.stats-legend-help'>Hover or tap a weapon to highlight its line, and use the arrow to open the part. Only parts tagged pistol or rifle are included.</Text>
                </div>
            </div>
        );
    }

    renderComparisonChart(stats, chartWidth, darkmode) {
        const { currentYearData, previousYearData, comparisonTicks } = stats.comparison;
        if (currentYearData.length === 0) {
            return null;
        }
        const hasComparisonData = previousYearData.length > 0;
        const chartHeight = chartWidth < 576 ? 160 : 220;
        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='font-weight-lighter text-body-secondary mb-1'>
                    <Text id='part.shotCountComparison'>Monthly usage comparison</Text>
                    {hasComparisonData && (
                        <span class='ms-2'>
                            (<span style='color: #3b82f6;'>●</span> <Text id='part.currentYear'>Current 12 months</Text>
                            {' '}<span style='color: #f59e0b;'>●</span> <Text id='part.previousYear'>Previous 12 months</Text>)
                        </span>
                    )}
                </div>
                <MiniGraph
                    stores={this.props.stores}
                    dataLeft={hasComparisonData ? [currentYearData, previousYearData] : [currentYearData]}
                    legendLeft={[]}
                    width={chartWidth}
                    height={chartHeight}
                    xTicks={comparisonTicks}
                    xTicksVal={monthTick}
                    yTicksLeftVal={val => Math.round(val)}
                    showYTicksRight={0}
                    paddingLeft={50}
                    paddingRight={20}
                    paddingBottom={15}
                    yTicksLeftWidth={'45px'}
                    xTicksStyle={'font-size: 11px; font-weight: 400; color: #5f6368 !important;'}
                    yTicksLeftStyle={'color: #5f6368 !important;'}
                    inputStyleLeft={hasComparisonData
                        ? ['stroke: #3b82f6 !important; stroke-width: 2px !important;', 'stroke: #f59e0b !important; stroke-width: 2px !important; stroke-dasharray: 4,4;']
                        : ['stroke: #3b82f6 !important; stroke-width: 2px !important;']
                    }
                    smoothLeft={hasComparisonData ? [true, true] : [true]}
                    gradientFillLeft={['#3b82f6']}
                    gradientOpacity={0.1}
                />
            </div>
        );
    }

    render() {
        const { chartWidth, period } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const stats = allWeaponsStats(this.getParts(), new Date(), period);
        const weaponCount = stats.weapons.length;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>
                    <div class='mb-3'>
                        <h5 class='mb-1'>
                            <i class='fa-duotone fa-chart-line me-2' />
                            <Text id='part.stats-title'>Shot statistics for all weapons</Text>
                        </h5>
                        {stats.total > 0 && (
                            <div class='text-body-secondary'>
                                <Text id='part.shotCount'>Total shot count</Text>: <strong class='text-body'>{formatNumber(stats.total)}</strong>
                                <span class='mx-2'>·</span>
                                <Text id='part.stats-weapons-with-shots' fields={{ count: weaponCount }}>{weaponCount} weapons with logged shots</Text>
                            </div>
                        )}
                        {this.renderPeriodSelector()}
                    </div>

                    <div class='w-100' ref={c => this.containerRef = c}>
                        {stats.total === 0 ? (
                            <div class='alert alert-info'>
                                {period === 'all'
                                    ? <Text id='part.stats-no-data'>No shots have been logged on parts tagged pistol or rifle yet.</Text>
                                    : <Text id='part.stats-no-data-period'>No shots have been logged in this period.</Text>}
                            </div>
                        ) : (
                            <>
                                {this.renderUsageChart(stats, chartWidth, darkmode)}
                                {this.renderComparisonChart(stats, chartWidth, darkmode)}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default PartStatsDrawer;
